import { isBrowser } from "@chatfood/core-utils";
import {
  ButtonClickTrackingPlugin,
  enableButtonClickTracking,
} from "@snowplow/browser-plugin-button-click-tracking";
import { ClientHintsPlugin } from "@snowplow/browser-plugin-client-hints";
import {
  enableErrorTracking,
  ErrorTrackingPlugin,
} from "@snowplow/browser-plugin-error-tracking";
import {
  enableFormTracking,
  FormTrackingPlugin,
} from "@snowplow/browser-plugin-form-tracking";
import {
  enableLinkClickTracking,
  LinkClickTrackingPlugin,
} from "@snowplow/browser-plugin-link-click-tracking";
import { PerformanceNavigationTimingPlugin } from "@snowplow/browser-plugin-performance-navigation-timing";
import { WebVitalsPlugin } from "@snowplow/browser-plugin-web-vitals";
import {
  addGlobalContexts,
  addPlugin,
  enableActivityTracking,
  newTracker,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import type { ClientSession } from "@snowplow/browser-tracker";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
import type { Analytics } from "../analytics";
import { Provider } from "../provider";
import type { ProviderListeners } from "../provider-listeners";

type IConfig = {
  collector: string;
  postPath: string;
  appId: string;
  appVersion: string;
  schemaVendor: string;
};

export class Snowplow extends Provider {
  public key = "snowplow";
  protected analytics: Analytics | undefined;
  private config: IConfig;
  private journeySessionId: string | undefined;

  protected listeners: ProviderListeners = {
    setSuperProperties: (payload) => {
      // Use a generic data schema with typed context
      this.addGlobalContext(
        "business",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "business",
          account_id: payload.businessId,
          data: JSON.stringify({
            businessId: payload.businessId,
            businessName: payload.businessName,
            outletId: payload.outletId,
            outletName: payload.outletName,
            orderingMode: payload.orderingMode,
            salesChannel: payload.salesChannel,
          }),
        },
      );

      return payload;
    },

    userAnonymize: (payload) => {
      setUserId(null);

      // Use a generic data schema with typed context
      this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: "",
            email: "",
          }),
        },
      );

      return payload;
    },

    userIdentify: (payload) => {
      setUserId(payload.id);

      // Use a generic data schema with typed context
      this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: payload.id,
            email: payload.email ?? "",
          }),
        },
      );

      return payload;
    },

    pageView: (payload) => {
      if (!isBrowser()) return payload;

      trackPageView();
      return payload;
    },

    viewMenu: (payload) => {
      this.trackCustomEvent("menu", "Viewed Menu", {
        menuId: payload.id,
        menuSlug: payload.slug,
      });
      return payload;
    },

    viewItem: (payload) => {
      this.trackCustomEvent("item", "Viewed Item", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        flow: payload.flow,
      });
      return payload;
    },

    addItemToBasket: (payload) => {
      this.trackCustomEvent("basket", "Added Item to Basket", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        quantity: payload.quantity,
        plu: payload.plu,
        flow: payload.flow,
        upsellReference: payload.upsellReference,
      });
      return payload;
    },

    removeItemFromBasket: (payload) => {
      this.trackCustomEvent("basket", "Removed Item from Basket", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        quantity: payload.quantity,
      });
      return payload;
    },

    initiateCheckout: (payload) => {
      this.trackCustomEvent("checkout", "Initiated Checkout", {
        discountCode: payload.discountCode ?? "",
        total: payload.total.value,
        currency: payload.total.currency,
        itemCount: payload.items.length,
      });
      return payload;
    },

    addPaymentInfo: (payload) => {
      this.trackCustomEvent("checkout", "Added Payment Info", {
        method: payload.method,
      });
      return payload;
    },

    selectTipAmount: (payload) => {
      this.trackCustomEvent("checkout", "Selected Tip Amount", {
        amount: payload.amount.value,
        currency: payload.amount.currency,
      });
      return payload;
    },

    placeOrder: (payload) => {
      this.trackCustomEvent("order", "Placed Order Details", {
        orderId: payload.orderId,
        orderUid: payload.orderUid,
        total: payload.total.value,
        tax: payload.tax.value,
        deliveryFee: payload.deliveryFee.value,
        tips: payload.tips.value,
        discount: payload.discount.value,
        loyalty: payload.loyalty.value,
        serviceCharges: payload.serviceCharges.value + payload.bagFee.value,
        currency: payload.total.currency,
        discountCode: payload.discountCode ?? "",
        orderingMode: payload.orderingMode,
        salesChannel: payload.salesChannel,
        itemCount: payload.items.length,
        packagingType: payload.packagingType ?? "",
      });
      return payload;
    },

    orderSuccess: (payload) => {
      this.trackCustomEvent("order", "Placed Order", {
        orderUid: payload.orderUid,
      });
      return payload;
    },

    orderFailure: (payload) => {
      this.trackCustomEvent("order", "Place Order Failed", {
        orderUid: payload.orderUid,
        reason: payload.reason ?? "Unknown",
      });
      return payload;
    },

    viewCrossSell: (payload) => {
      this.trackCustomEvent("crosssell", "Viewed Cross-Sell", {
        flow: "cross-sell",
        surface: payload.surface,
        itemCount: payload.itemCount,
        upsellReference: payload.upsellReference,
      });
      return payload;
    },
  };

  constructor(config: IConfig) {
    super();
    this.config = config;
  }

  protected onInstall() {
    // Only run in browser (not during SSR or tests)
    if (!isBrowser()) return;

    if (!this.config.collector) {
      throw new Error("No Snowplow token provided");
    }

    Object.assign(window, {
      webVitals: { onCLS, onFCP, onINP, onLCP, onTTFB },
    });

    newTracker("sp", this.config.collector, {
      appId: this.config.appId,
      appVersion: this.config.appVersion,
      postPath: this.config.postPath || undefined,
      platform: "web",
      discoverRootDomain: true,
      cookieSameSite: "None",
      sessionCookieTimeout: 30 * 60,
      onSessionUpdateCallback: (session) => this.trackStartedJourney(session),
      contexts: {
        webPage: true,
        session: true, // Enable session context entity
        browser: true,
      },
      plugins: [
        WebVitalsPlugin({ loadWebVitalsScript: false }),
        ErrorTrackingPlugin(),
        PerformanceNavigationTimingPlugin(),
        ButtonClickTrackingPlugin(),
        LinkClickTrackingPlugin(),
        FormTrackingPlugin(),
        ClientHintsPlugin(true),
      ],
    });

    enableActivityTracking({ minimumVisitLength: 15, heartbeatDelay: 10 });
    enableErrorTracking();
    enableButtonClickTracking();
    enableLinkClickTracking();
    enableFormTracking({
      options: {
        events: ["submit_form"],
        fields: { transform: () => null },
      },
    });
    void this.detectBots();
  }

  protected async detectBots() {
    const { BotDetectionPlugin } =
      await import("@snowplow/browser-plugin-bot-detection");

    addPlugin({ plugin: BotDetectionPlugin() });
  }

  private trackStartedJourney({ sessionId }: ClientSession) {
    if (sessionId === this.journeySessionId) return;

    this.journeySessionId = sessionId;
    setTimeout(() => {
      this.trackCustomEvent("journey", "Started Journey", {});
    });
  }

  protected trackCustomEvent(
    context: string,
    action: string,
    payload: Record<string, unknown>,
  ) {
    trackSelfDescribingEvent({
      event: {
        schema: `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        data: {
          context_type: context,
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            action,
            ...payload,
          }),
        },
      },
    });
  }

  protected addGlobalContext(
    contextKey: string,
    schema: string,
    payload: Record<string, unknown>,
  ) {
    const contextEntity = {
      schema,
      data: payload,
    };
    addGlobalContexts({ [contextKey]: contextEntity });
  }
}
